import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {FetchGeoJsonResponse, IFetchReportsPayload, IGeographyFilterPayload} from '../types';
import download from '../../../../lib/download';

/**
 * Manages report downloads (providers, available providers, adequacy scores, standard reporting)
 * and report provider GeoJSON for map visualization. Tracks download progress per report item.
 */
export class ReportStore {
    private _reportProviderGeoJson: FetchGeoJsonResponse | null = null;
    private _reportProviderGeoJsonError: string | null = null;
    private _reportProviderGeoJsonLoading = false;
    downloadingItems: {[itemName: string]: boolean} = {};

    constructor() {
        makeAutoObservable(this);
    }

    get reportProviderGeoJson(): FetchGeoJsonResponse | null {
        return this._reportProviderGeoJson;
    }

    get reportProviderGeoJsonError(): string | null {
        return this._reportProviderGeoJsonError;
    }

    set reportProviderGeoJsonError(value: string | null) {
        this._reportProviderGeoJsonError = value;
    }

    get reportProviderGeoJsonLoading(): boolean {
        return this._reportProviderGeoJsonLoading;
    }

    set reportProviderGeoJsonLoading(value: boolean) {
        this._reportProviderGeoJsonLoading = value;
    }

    async downloadReport(
        payload: IFetchReportsPayload,
        reportType: 'providers' | 'available-providers' | 'adequacy-scores' | 'standard-reporting',
        itemName: string,
        handleError: (error: unknown, title: string, retryCallback?: () => void) => void
    ): Promise<void> {
        runInAction(() => {
            this.downloadingItems[itemName] = true;
        });
        try {
            let response;
            let filename;

            switch (reportType) {
            case 'providers':
                response = await networkOptimization.download_providers_report(payload);
                filename = 'providers-report.zip';
                break;
            case 'available-providers':
                response = await networkOptimization.download_available_providers_report(payload);
                filename = 'available-providers-report.zip';
                break;
            case 'adequacy-scores':
                response = await networkOptimization.download_adequacy_scores_report(payload);
                filename = 'adequacy-scores-report.zip';
                break;
            case 'standard-reporting':
                response = await networkOptimization.download_standard_report(payload);
                filename = 'standard-reporting.zip';
                break;
            default:
                throw new Error(`Unknown report type: ${reportType}`);
            }

            download(response, filename, 'application/zip');
        } catch (error) {
            handleError(
                error,
                'Download Failed',
                () => this.downloadReport(payload, reportType, itemName, handleError)
            );
        } finally {
            runInAction(() => {
                this.downloadingItems[itemName] = false;
            });
        }
    }

    async downloadComplianceSummary(
        itemName: string,
        runId: number,
        revisionId: number,
        geographyFilters: IGeographyFilterPayload,
        distanceCalcType: string,
        appliedGeographyFilter: {state: string; county: string},
        signal: AbortSignal,
        handleError: (error: unknown, title: string, retryCallback?: () => void) => void
    ): Promise<void> {
        runInAction(() => {
            this.downloadingItems[itemName] = true;
        });
        try {
            const response = await networkOptimization.download_compliance_summary(
                runId,
                revisionId,
                geographyFilters,
                distanceCalcType,
                signal
            );
            const {state, county} = appliedGeographyFilter;
            const level = state && county ? 'specialty' : state ? 'county' : 'state';
            download(response, `compliance-summary-${level}.csv`, 'text/csv');
        } catch (error) {
            handleError(
                error,
                'Download Failed',
                () => this.downloadComplianceSummary(itemName, runId, revisionId, geographyFilters, distanceCalcType, appliedGeographyFilter, signal, handleError)
            );
        } finally {
            runInAction(() => {
                this.downloadingItems[itemName] = false;
            });
        }
    }

    resetReportProviderGeoJson(): void {
        this._reportProviderGeoJson = null;
        this._reportProviderGeoJsonError = null;
        this._reportProviderGeoJsonLoading = false;
    }
}
